import { z } from 'zod';
import { getMinAgeForLegalCountry } from '../../memberships/utils/min-age';
import type { Owner } from '../types/state';

const minAgeDate = (legalCountry?: string | null): Date => {
  const d = new Date();
  d.setUTCFullYear(d.getUTCFullYear() - getMinAgeForLegalCountry(legalCountry));
  return d;
};

const buildBirthdateValidator = (legalCountry?: string | null) =>
  z
    .string()
    .date()
    .transform(v => new Date(v))
    .pipe(z.date().min(new Date('1900-01-01')).max(minAgeDate(legalCountry)));

export function validateBirthdate(value: string, legalCountry?: string | null): boolean {
  return buildBirthdateValidator(legalCountry).safeParse(value).success;
}

export const buildPersonalInfoSchema = (legalCountry?: string | null) =>
  z.object({
    gender: z.string().nonempty(),
    birthdate: z
      .string()
      .nonempty()
      .refine(v => validateBirthdate(v, legalCountry), { message: 'invalid' }),
    birthCity: z.string().nonempty(),
    birthCountry: z.string().nonempty(),
    birthZipcode: z.string().nonempty(),
    nationality: z.string().nonempty(),
  });

export const personalInfoSchema = buildPersonalInfoSchema();

export type PersonalInfoSchema = z.infer<typeof personalInfoSchema>;

export const addressSchema = z.object({
  addressLine1: z.string().nonempty(),
  addressLine2: z.string().optional(),
  addressCity: z.string().nonempty(),
  addressCountry: z.string().nonempty(),
  addressZipcode: z.string().nonempty(),
});

export type AddressSchema = z.infer<typeof addressSchema>;

export const personalInfoWithAddressSchema = z.object({
  ...personalInfoSchema.shape,
  ...addressSchema.shape,
});

export type PersonalInfoWithAddressSchema = z.infer<typeof personalInfoWithAddressSchema>;

export function hasPersonalInfo(owner: Owner, legalCountry?: string | null): boolean {
  return buildPersonalInfoSchema(legalCountry).safeParse({
    gender: owner.gender,
    birthdate: owner.birthdate,
    birthCity: owner.birthCity,
    birthCountry: owner.birthCountry,
    birthZipcode: owner.birthZipcode,
    nationality: owner.nationality,
  }).success;
}
